import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { markNavigationStart, markNavigationEnd } from "@/lib/performanceMonitoring";
import { useAuth } from "@/hooks/useAuth";
import { getGeoData } from "@/lib/countryContext";

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getSessionId(userId?: string): string {
  const key = "vt_sid";
  let sid = sessionStorage.getItem(key);
  if (!sid || (userId && !sid.startsWith(userId))) {
    sid = userId ? `${userId}-${crypto.randomUUID()}` : crypto.randomUUID();
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

async function sendHeartbeat(path: string, country: string | null, userId: string) {
  try {
    const sid = getSessionId(userId);
    await supabase.from("user_presence").upsert(
      {
        session_id: sid,
        page_path: path,
        device_type: getDeviceType(),
        country: country,
        last_seen_at: new Date().toISOString(),
      } as any,
      { onConflict: "session_id" }
    );
  } catch {
    // silent
  }
}

export function usePageTracking() {
  const location = useLocation();
  const { user } = useAuth();
  const lastPath = useRef<string>("");
  const countryRef = useRef<string | null>(null);

  // Geo lookup once per session — shared cache, no duplicate calls
  useEffect(() => {
    if (!countryRef.current) {
      getGeoData().then((geo) => {
        countryRef.current = geo.country_name || null;
      });
    }
  }, []);

  // Page view tracking (works for anon + authenticated)
  useEffect(() => {
    const path = location.pathname;
    if (path === lastPath.current) return;

    // Performance: mark end of previous navigation, start new one
    if (lastPath.current) markNavigationEnd(lastPath.current);
    markNavigationStart();

    lastPath.current = path;

    const track = async () => {
      try {
        const country = countryRef.current;
        await supabase.from("page_views").insert({
          page_path: path,
          country,
          city: null,
          device_type: getDeviceType(),
          referrer: document.referrer || null,
          session_id: getSessionId(),
        } as any);
      } catch {
        // silent
      }
    };

    const timer = setTimeout(track, 1000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Heartbeat for presence - only for authenticated users
  useEffect(() => {
    if (!user) return;
    sendHeartbeat(location.pathname, countryRef.current, user.id);
    const interval = setInterval(() => {
      sendHeartbeat(location.pathname, countryRef.current, user.id);
    }, 30000);
    return () => clearInterval(interval);
  }, [location.pathname, user]);
}
